const POPUP_WIDTH = 500;
const POPUP_HEIGHT = 600;
const POLL_INTERVAL_MS = 100;
const TIMEOUT_MS = 5 * 60 * 1000;
const DETACH_TIMEOUT_MS = 10_000;

export function openPopup(): Window {
  const left = window.screenX + (window.outerWidth - POPUP_WIDTH) / 2;
  const top = window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2;

  const popup = window.open(
    'about:blank',
    `auth0-popup-${Date.now()}`,
    `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`,
  );

  if (!popup) {
    throw new Error(
      'Popup blocked. Please allow popups for this site and try again.',
    );
  }

  return popup;
}

export async function detachPopupOpener(popup: Window): Promise<void> {
  try {
    popup.opener = null;
    return;
  } catch {
    // Auth0 leaves the reusable popup on its cross-origin web-message page.
    // Navigate it back to our origin before clearing opener for the provider.
    popup.location.href = window.location.origin;
  }

  await new Promise<void>((resolve, reject) => {
    const startTime = Date.now();
    const interval = setInterval(() => {
      if (popup.closed) {
        clearInterval(interval);
        reject(new Error('cancelled'));
        return;
      }

      if (Date.now() - startTime > DETACH_TIMEOUT_MS) {
        clearInterval(interval);
        reject(new Error('Failed to prepare provider popup'));
        return;
      }

      try {
        if (new URL(popup.location.href).origin !== window.location.origin) {
          return;
        }
        popup.opener = null;
        clearInterval(interval);
        resolve();
      } catch {
        // The bridge navigation is still cross-origin, keep polling.
      }
    }, POLL_INTERVAL_MS);
  });
}

export function pollForOAuthCallback(
  popup: Window,
  redirectOrigin: string,
): Promise<URLSearchParams> {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();

    const interval = setInterval(() => {
      if (Date.now() - startTime > TIMEOUT_MS) {
        clearInterval(interval);
        popup.close();
        reject(new Error('OAuth flow timed out'));
        return;
      }

      if (popup.closed) {
        clearInterval(interval);
        reject(new Error('cancelled'));
        return;
      }

      try {
        const popupUrl = new URL(popup.location.href);
        const hasOAuthResult =
          popupUrl.searchParams.has('connect_code') ||
          popupUrl.searchParams.has('error');
        if (popupUrl.origin === redirectOrigin && hasOAuthResult) {
          clearInterval(interval);
          popup.close();
          resolve(popupUrl.searchParams);
        }
      } catch {
        // SecurityError: popup is on cross-origin page, keep polling
      }
    }, POLL_INTERVAL_MS);
  });
}
